import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useEntity } from '../contexts/EntityContext';
import { useAuth } from '../contexts/AuthContext';
import { userService, UsuarioInfoPorEntidadeResponse } from '../services/userService';

interface ModuleSelectionComponentProps {
    onModuleSelect?: (moduleType: string) => void;
}

const ModuleSelectionComponent: React.FC<ModuleSelectionComponentProps> = ({
    onModuleSelect
}) => {
    const { currentEntity } = useEntity();
    const { user, loading } = useAuth();
    const [reloadKey, setReloadKey] = useState(0);
    const [userInfo, setUserInfo] = useState<UsuarioInfoPorEntidadeResponse[]>([]);
    const [userInfoLoading, setUserInfoLoading] = useState(false);

    useEffect(() => {
        setReloadKey(prev => prev + 1);

        if (currentEntity?.entidade_id) {
            fetchUserInfo();
        }
    }, [currentEntity?.entidade_id]);

    const fetchUserInfo = async () => {
        if (!currentEntity?.entidade_id) return;

        try {
            setUserInfoLoading(true);
            const data = await userService.getUserInfoByEntidade(currentEntity.entidade_id);
            setUserInfo(data);
        } catch (error) {
            console.error('Error fetching user info by entity:', error);
            setUserInfo([]);
        } finally {
            setUserInfoLoading(false);
        }
    };

    const handleAcoesClick = () => {
        if (onModuleSelect) {
            onModuleSelect('acoes');
        }
    };

    const handleAuditoriaClick = () => {
        if (onModuleSelect) {
            onModuleSelect('auditoria');
        }
    };

    const handleVisualizadorGeralClick = () => {
        if (onModuleSelect) {
            onModuleSelect('visualizadorgeral');
        }
    };

    const handleIndicadoresPortalClick = () => {
        window.open('https://portal.katru.com.br/auth/login', '_blank');
    };

    const hasAcoesPermission = userInfo.some(info =>
        info.permissoes.includes('modulo.acoes.view')
    );

    const hasConformidadePermission = userInfo.some(info =>
        info.permissoes.includes('modulo.conformidade.view')
    );

    const hasVisualizadorGeralPermission = userInfo.some(info =>
        info.permissoes.includes('modulo.visualizadorgeral.view')
    );

    if (loading || userInfoLoading) {
        return (
            <Container className="mt-4">
                <div>Carregando...</div>
            </Container>
        );
    }

    return (
        <Container className="mt-4" key={reloadKey}>
            <div className="mb-4">
                <h4>Olá, {user?.name || 'Usuário'}</h4>
                <h4>Selecione um módulo</h4>
            </div>
            <div className="mb-5">
                <h5 className="mb-3">Módulos Disponíveis</h5>
                <Row className="g-4">
                    {hasVisualizadorGeralPermission && (
                        <Col md={6}>
                            <Card
                                className="entity-card"
                                onClick={handleVisualizadorGeralClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <Card.Body className="d-flex justify-content-between align-items-center p-4">
                                    <div>
                                        <Card.Title className="text-dark mb-2">VISUALIZADOR GERAL</Card.Title>
                                        <Card.Text className="text-muted mb-0">
                                            Visualize informações gerais da entidade
                                        </Card.Text>
                                    </div>
                                    <div className="icon-container" style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.5rem',
                                        color: '#000000',
                                        transition: 'all 0.2s ease'
                                    }}>
                                        <i className="mdi mdi-eye" style={{ fontSize: '2rem' }}></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                    {hasAcoesPermission && (
                        <Col md={6}>
                            <Card
                                className="entity-card"
                                onClick={handleAcoesClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <Card.Body className="d-flex justify-content-between align-items-center p-4">
                                    <div>
                                        <Card.Title className="text-dark mb-2">GERENCIADOR DE AÇÕES</Card.Title>
                                        <Card.Text className="text-muted mb-0">
                                            Gerencie e acompanhe as ações da entidade
                                        </Card.Text>
                                    </div>
                                    <div className="icon-container" style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.5rem',
                                        color: '#000000',
                                        transition: 'all 0.2s ease'
                                    }}>
                                        <i className="mdi mdi-chart-bar" style={{ fontSize: '2rem' }}></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                    {hasConformidadePermission && (
                        <Col md={6}>
                            <Card
                                className="entity-card"
                                onClick={handleAuditoriaClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <Card.Body className="d-flex justify-content-between align-items-center p-4">
                                    <div>
                                        <Card.Title className="text-dark mb-2">MONITORAMENTO</Card.Title>
                                        <Card.Text className="text-muted mb-0">
                                            Verifique a conformidade dos processos
                                        </Card.Text>
                                    </div>
                                    <div className="icon-container" style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.5rem',
                                        color: '#000000',
                                        transition: 'all 0.2s ease'
                                    }}>
                                        <i className="mdi mdi-clipboard-text" style={{ fontSize: '2rem' }}></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                    <Col md={6}>
                        <Card
                            className="entity-card"
                            onClick={handleIndicadoresPortalClick}
                            style={{ cursor: 'pointer' }}
                        >
                            <Card.Body className="d-flex justify-content-between align-items-center p-4">
                                <div>
                                    <Card.Title className="text-dark mb-2">INDICADORES PORTAL KATRU</Card.Title>
                                    <Card.Text className="text-muted mb-0">
                                        Acesse os indicadores no Portal Katru
                                    </Card.Text>
                                </div>
                                <div className="icon-container" style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.5rem',
                                    color: '#000000',
                                    transition: 'all 0.2s ease'
                                }}>
                                    <i className="mdi mdi-poll" style={{ fontSize: '2rem' }}></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Container>
    );
};

export default ModuleSelectionComponent;
